import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Box, CustomSpinner, Text } from '../../commonComponents';
import { getChallengeRedirectPath } from '../../services/interceptors/cloudflareChallenge';

/**
 * CloudflareChallenge Component
 *
 * This component handles the Cloudflare managed challenge page.
 * When a user is redirected here due to a 403 challenge response,
 * Cloudflare will automatically present the challenge (CAPTCHA/Turnstile).
 * Once completed, the user is redirected back to their original location.
 */
export const CloudflareChallenge: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [checkingStatus, setCheckingStatus] = useState(true);
  const redirTo = searchParams.get('redirTo') || '/';

  useEffect(() => {
    // Check if the cf_clearance cookie has been set
    const checkChallengeCompletion = () => {
      // Check if we have the cf_clearance cookie
      const hasClearanceCookie = document.cookie
        .split(';')
        .some((item) => item.trim().startsWith('cf_clearance='));

      if (hasClearanceCookie) {
        // Challenge completed, redirect back
        const redirectPath = getChallengeRedirectPath() || redirTo;
        window.location.href = redirectPath;
      } else {
        // Keep checking
        setCheckingStatus(false);
      }
    };

    // Initial check
    checkChallengeCompletion();

    // Set up an interval to check periodically
    const interval = setInterval(checkChallengeCompletion, 1000);

    return () => clearInterval(interval);
  }, [redirTo]);

  if (checkingStatus) {
    return <CustomSpinner isLoading={true} />;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      padding={3}
      textAlign="center"
    >
      <Text fontSize={24} fontWeight="bold" marginBottom={16}>
        Complete Security Check
      </Text>
      <Text fontSize={16} color="gray" marginBottom={24}>
        Please complete the security challenge below to continue.
      </Text>
      <Text fontSize={14} color="gray">
        You will be automatically redirected once the challenge is completed.
      </Text>
    </Box>
  );
};